import * as d3 from 'd3';
import type { Dispatch, SetStateAction } from 'react';
import { Block, BlockGeometry } from '../../types';
import { getBlockDimensions } from '../../utils/blockUtils';
import { updateBpmnActivityGeometry } from '../../utils/bpmnShapeRenderer';
import { layoutDecisionDiamondText } from '../../utils/decisionLabelLayout';
import { STYLES } from '../../styles/constants';

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const MIN_WIDTH = 48;
const MIN_HEIGHT = 36;

const HANDLES: Array<{
  dir: ResizeDir;
  cursor: string;
  left: string;
  top: string;
  width: string;
  height: string;
  translate: string;
}> = [
  { dir: 'n', cursor: 'ns-resize', left: '50%', top: '0%', width: 'calc(100% - 16px)', height: '10px', translate: '-50% -50%' },
  { dir: 's', cursor: 'ns-resize', left: '50%', top: '100%', width: 'calc(100% - 16px)', height: '10px', translate: '-50% -50%' },
  { dir: 'e', cursor: 'ew-resize', left: '100%', top: '50%', width: '10px', height: 'calc(100% - 16px)', translate: '-50% -50%' },
  { dir: 'w', cursor: 'ew-resize', left: '0%', top: '50%', width: '10px', height: 'calc(100% - 16px)', translate: '-50% -50%' },
  { dir: 'ne', cursor: 'nesw-resize', left: '100%', top: '0%', width: '12px', height: '12px', translate: '-50% -50%' },
  { dir: 'nw', cursor: 'nwse-resize', left: '0%', top: '0%', width: '12px', height: '12px', translate: '-50% -50%' },
  { dir: 'se', cursor: 'nwse-resize', left: '100%', top: '100%', width: '12px', height: '12px', translate: '-50% -50%' },
  { dir: 'sw', cursor: 'nesw-resize', left: '0%', top: '100%', width: '12px', height: '12px', translate: '-50% -50%' },
];

function lockAspect(block: Block): boolean {
  if (block.type === 'start' || block.type === 'end' || block.type === 'decision') return true;
  if (block.type === 'process' && (block.bpmnSymbol || block.dmnSymbol || block.vsmSymbol)) return true;
  return false;
}

function getCanvasScale(): number {
  const el = document.getElementById('swimlane-container');
  if (!el) return 1;
  const transform = window.getComputedStyle(el).transform;
  if (!transform || transform === 'none') return 1;
  const matrix = transform.match(/matrix\(([^)]+)\)/);
  if (matrix) {
    const value = parseFloat(matrix[1].split(',')[0]);
    if (!isNaN(value) && value > 0) return value;
  }
  const scaleMatch = transform.match(/scale\(([^)]+)\)/);
  if (scaleMatch) {
    const value = parseFloat(scaleMatch[1]);
    if (!isNaN(value) && value > 0) return value;
  }
  return 1;
}

function snapResizeToGrid(
  geom: { x: number; y: number; width: number; height: number },
  dir: ResizeDir,
  anchors: { left: number; top: number; right: number; bottom: number }
) {
  const grid = STYLES.grid.size;
  const snap = (value: number) => Math.round(value / grid) * grid;
  let { x, y, width, height } = geom;

  if (dir.includes('e')) {
    width = Math.max(MIN_WIDTH, snap(width));
  }
  if (dir.includes('w')) {
    width = Math.max(MIN_WIDTH, snap(width));
    x = snap(anchors.right - width);
    width = Math.max(MIN_WIDTH, anchors.right - x);
  }
  if (dir.includes('s')) {
    height = Math.max(MIN_HEIGHT, snap(height));
  }
  if (dir.includes('n')) {
    height = Math.max(MIN_HEIGHT, snap(height));
    y = snap(anchors.bottom - height);
    height = Math.max(MIN_HEIGHT, anchors.bottom - y);
  }

  return { x, y, width, height };
}

function applyGeometryToDom(
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  block: Block,
  geom: { x: number; y: number; width: number; height: number }
) {
  container
    .style('left', `${geom.x}px`)
    .style('top', `${geom.y}px`)
    .style('width', `${geom.width}px`)
    .style('height', `${geom.height}px`);

  const inner = container.select<HTMLDivElement>(`.${block.type}-block-${block.id}`);
  if (!inner.empty()) {
    inner
      .style('width', `${geom.width}px`)
      .style('height', `${geom.height}px`);
  }

  const svg = inner.select<SVGSVGElement>('svg');
  if (!svg.empty()) {
    if (block.bpmnSymbol === 'textAnnotation') {
      svg.attr('width', 12).attr('height', geom.height).attr('viewBox', `0 0 12 ${geom.height}`);
      const inset = 1.5;
      svg
        .select('path')
        .attr(
          'd',
          `M ${12 - inset} ${inset} L ${inset} ${inset} L ${inset} ${geom.height - inset} L ${12 - inset} ${geom.height - inset}`
        );
    } else {
      svg.attr('width', geom.width).attr('height', geom.height);
      updateBpmnActivityGeometry(svg, geom.width, geom.height);
      if (block.type === 'decision' && !block.bpmnSymbol) {
        const hw = geom.width / 2;
        const hh = geom.height / 2;
        svg
          .select('path')
          .attr('d', `M ${hw} 0 L ${geom.width} ${hh} L ${hw} ${geom.height} L 0 ${hh} Z`);
        layoutDecisionDiamondText(svg, block, geom.width, geom.height);
      }
    }
  }

  container
    .select('.bpmn-meaning-caption')
    .style('top', `${geom.height + 6}px`)
    .style('max-width', `${Math.max(geom.width + 16, 128)}px`);

  if (block.bpmnSymbol !== 'textAnnotation') {
    container
      .select(`[class*="-layers-${block.id}"] > div`)
      .style('top', `${geom.height + 10}px`)
      .style('width', `${geom.width}px`);
  }
}

export function implementResize(
  element: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  block: Block,
  onGeometryEnd: (id: string, geometry: BlockGeometry) => void,
  setBlocks: Dispatch<SetStateAction<Block[]>>,
  onResizeMove?: (id: string, geometry: BlockGeometry) => void
) {
  const startDims = getBlockDimensions(block);
  element
    .style('width', `${startDims.width}px`)
    .style('height', `${startDims.height}px`);

  const knobs: Array<d3.Selection<HTMLDivElement, unknown, null, undefined>> = [];
  let resizeMoveRaf: number | null = null;
  let pendingResizeGeometry: BlockGeometry | null = null;

  const scheduleResizeMove = (geometry: BlockGeometry) => {
    if (!onResizeMove) return;
    pendingResizeGeometry = geometry;
    if (resizeMoveRaf != null) return;
    resizeMoveRaf = requestAnimationFrame(() => {
      resizeMoveRaf = null;
      if (pendingResizeGeometry) {
        onResizeMove(block.id, pendingResizeGeometry);
        pendingResizeGeometry = null;
      }
    });
  };

  const cancelResizeMove = () => {
    if (resizeMoveRaf != null) {
      cancelAnimationFrame(resizeMoveRaf);
      resizeMoveRaf = null;
    }
    pendingResizeGeometry = null;
  };

  HANDLES.forEach((handle) => {
    const hit = element
      .append('div')
      .attr('class', `block-resize-handle block-resize-handle-${handle.dir}`)
      .attr('data-resize-dir', handle.dir)
      .style('position', 'absolute')
      .style('left', handle.left)
      .style('top', handle.top)
      .style('width', handle.width)
      .style('height', handle.height)
      .style('transform', `translate(${handle.translate})`)
      .style('cursor', handle.cursor)
      .style('z-index', '6')
      .style('background', 'transparent')
      .style('pointer-events', 'auto');

    const isEdge = handle.dir.length === 1;
    const knob = hit
      .append('div')
      .style('position', 'absolute')
      .style('left', '50%')
      .style('top', '50%')
      .style('width', isEdge ? (handle.dir === 'n' || handle.dir === 's' ? '18px' : '5px') : '8px')
      .style('height', isEdge ? (handle.dir === 'n' || handle.dir === 's' ? '5px' : '18px') : '8px')
      .style('transform', 'translate(-50%, -50%)')
      .style('border-radius', '1px')
      .style('background', '#3b82f6')
      .style('border', '1px solid #fff')
      .style('box-shadow', '0 0 0 1px rgba(59,130,246,0.35)')
      .style('opacity', '0')
      .style('pointer-events', 'none')
      .style('transition', 'opacity 120ms ease');

    knobs.push(knob);

    hit.on('mousedown.resize', (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const scale = getCanvasScale();
      const originX = event.clientX;
      const originY = event.clientY;
      const startX = parseFloat(element.style('left')) || block.x || 0;
      const startY = parseFloat(element.style('top')) || block.y || 0;
      const startW = parseFloat(element.style('width')) || block.width || startDims.width;
      const startH = parseFloat(element.style('height')) || block.height || startDims.height;
      const anchors = {
        left: startX,
        top: startY,
        right: startX + startW,
        bottom: startY + startH,
      };
      const aspect = startW / Math.max(startH, 1);
      const keepAspect = lockAspect(block) || event.shiftKey;

      const onMove = (moveEvent: MouseEvent) => {
        const dx = (moveEvent.clientX - originX) / scale;
        const dy = (moveEvent.clientY - originY) / scale;
        let x = startX;
        let y = startY;
        let width = startW;
        let height = startH;
        const dir = handle.dir;

        if (dir.includes('e')) width = startW + dx;
        if (dir.includes('s')) height = startH + dy;
        if (dir.includes('w')) {
          width = startW - dx;
          x = startX + dx;
        }
        if (dir.includes('n')) {
          height = startH - dy;
          y = startY + dy;
        }

        if (keepAspect) {
          if (dir === 'e' || dir === 'w') {
            height = width / aspect;
            if (dir === 'w') y = startY + (startH - height);
          } else if (dir === 'n' || dir === 's') {
            width = height * aspect;
            if (dir === 'n') x = startX + (startW - width);
          } else {
            height = width / aspect;
            if (dir.includes('n')) y = startY + startH - height;
            if (dir.includes('w')) x = startX + startW - width;
          }
        }

        if (width < MIN_WIDTH) {
          if (dir.includes('w')) x -= MIN_WIDTH - width;
          width = MIN_WIDTH;
        }
        if (height < MIN_HEIGHT) {
          if (dir.includes('n')) y -= MIN_HEIGHT - height;
          height = MIN_HEIGHT;
        }

        const geom = {
          x: Math.round(x),
          y: Math.round(y),
          width: Math.round(width),
          height: Math.round(height),
        };

        applyGeometryToDom(element, block, geom);
        Object.assign(block, geom);
        scheduleResizeMove(geom);
      };

      const onUp = () => {
        cancelResizeMove();
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        const geom: BlockGeometry = snapResizeToGrid(
          {
            x: block.x ?? startX,
            y: block.y ?? startY,
            width: block.width ?? startW,
            height: block.height ?? startH,
          },
          handle.dir,
          anchors
        );
        applyGeometryToDom(element, block, geom);
        Object.assign(block, geom);
        setBlocks((prev) =>
          prev.map((b) => (b.id === block.id ? { ...b, ...geom } : b))
        );
        onGeometryEnd(block.id, geom);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });
  });

  const showKnobs = () => knobs.forEach((knob) => knob.style('opacity', '1'));
  const hideKnobs = () => knobs.forEach((knob) => knob.style('opacity', '0'));

  element.on('mouseenter.resize', showKnobs);
  element.on('mouseleave.resize', hideKnobs);
}
